import { Insurance } from '@core/constants/data-types.constants';
import { InsuranceTarget } from '@core/models/insurance-target.model';
import { parseISO } from 'date-fns';
import { Config } from './config.model';
import { convert } from './currency-exchange';
import { FinancialData, getCurrentAge, LifePlan, PersonalData } from './life-plan.model';
import { getProductAvailableTemporalities } from './temporality.model';

export enum ConcreteInsuranceIds {
  DEATH_USER = 'death-user',
  DEATH_SPOUSE = 'death-spouse',
  SAVING_GENERAL = 'saving-general',
  SAVING_INVESTMENT = 'saving-investment',
  RETIREMENT_USER = 'retirement-user',
  RETIREMENT_SPOUSE = 'retirement-spouse'
}
export type ConcreteInsuranceId =
  | 'death-user'
  | 'death-spouse'
  | 'saving-general'
  | 'saving-investment'
  | 'retirement-user'
  | 'retirement-spouse';

export interface LifePlanInsurances {
  [key: string]: InsuranceTarget;
}

export interface SavingsInsurance {
  [key: string]: InsuranceTarget;
}

export interface RetirementInsurance {
  user: InsuranceTarget;
  spouse?: InsuranceTarget;
}

export function generateLifePlanInsurance(lifePlan: LifePlan, config: Config): LifePlanInsurances {
  const userPersonalData = lifePlan.user?.personal;
  const spousePersonalData = lifePlan.spouse?.personal;
  const userFinancialData = lifePlan.user?.financial;
  const spouseFinancialData = lifePlan.spouse?.financial;
  let deathInsurances;
  let educationInsurances;
  let savingsInsurances;
  let retirementInsurances;
  switch (lifePlan.type) {
    case 'DEATH':
      deathInsurances = generateDeathInsurances(
        config,
        userPersonalData,
        spousePersonalData,
        userFinancialData,
        spouseFinancialData
      );
      break;
    case 'EDUCATION':
      educationInsurances = generateEducationInsurances(config, userPersonalData, userFinancialData);
      break;
    case 'SAVING':
      savingsInsurances = generateSavingsInsurances(config, userPersonalData, userFinancialData);
      break;
    case 'RETIREMENT':
      retirementInsurances = generateRetirementInsurances(
        config,
        userPersonalData,
        spousePersonalData,
        userFinancialData,
        spouseFinancialData
      );
      break;
  }
  return {
    ...deathInsurances,
    ...educationInsurances,
    ...savingsInsurances,
    ...retirementInsurances
  };
}

export function generateLifePlanInsurances(lifePlan: LifePlan, config: Config): LifePlanInsurances {
  const userPersonalData = lifePlan.user?.personal;
  const spousePersonalData = lifePlan.spouse?.personal;
  const userFinancialData = lifePlan.user?.financial;
  const spouseFinancialData = lifePlan.spouse?.financial;
  return {
    ...generateDeathInsurances(config, userPersonalData, spousePersonalData, userFinancialData, spouseFinancialData),
    ...generateEducationInsurances(config, userPersonalData, userFinancialData),
    ...generateSavingsInsurances(config, userPersonalData, userFinancialData),
    ...generateRetirementInsurances(
      config,
      userPersonalData,
      spousePersonalData,
      userFinancialData,
      spouseFinancialData
    )
  };
}

export function generateDeathInsurances(
  config: Config,
  userPersonalData?: PersonalData,
  spousePersonalData?: PersonalData,
  userFinancialData?: FinancialData,
  spouseFinancialData?: FinancialData
): LifePlanInsurances {
  let userAge = 0;
  if (userPersonalData?.birthDate) {
    const age = getCurrentAge(userPersonalData.birthDate);
    if (age) {
      userAge = age;
    }
  }
  const userInvestorProfile =
    userFinancialData?.investorProfile ||
    config.sourceConfigurations?.investorProfileOptions.find(i => i.isDefault)?.identifier ||
    '';
  const deathInsurances: LifePlanInsurances = {};
  deathInsurances[ConcreteInsuranceIds.DEATH_USER] = {
    id: ConcreteInsuranceIds.DEATH_USER,
    order: 0,
    name: userPersonalData?.name || 'DEATH_INSURANCE.UNKNOWN_USER_NAME',
    surnames: userPersonalData?.surnames || '',
    type: 'time-period',
    insurance: 'DEATH',
    policyHolder: {
      age: userAge,
      gender: userPersonalData?.gender || '',
      investorProfile: userInvestorProfile,
      netMonthlyIncome: userFinancialData?.netMonthlyIncome
    },
    insured: {
      age: userAge
    },
    duration: {
      years: 5
    },
    periodLabel: 'DEATH_INSURANCE.SAFETY_NETWORK_PERIOD'
  };

  if (spousePersonalData) {
    let spouseAge = 0;
    if (spousePersonalData?.birthDate) {
      const age = getCurrentAge(spousePersonalData.birthDate);
      if (age) {
        spouseAge = age;
      }
    }
    deathInsurances[ConcreteInsuranceIds.DEATH_SPOUSE] = {
      id: ConcreteInsuranceIds.DEATH_SPOUSE,
      name: spousePersonalData.name || 'DEATH_INSURANCE.UNKNOWN_SPOUSE_NAME',
      order: 1,
      type: 'time-period',
      insurance: 'DEATH',
      policyHolder: {
        age: spouseAge,
        gender: spousePersonalData?.gender || '',
        // Investment products need the policy holder investment profile and spouse does not have investment profile
        investorProfile: userInvestorProfile,
        netMonthlyIncome: spouseFinancialData?.netMonthlyIncome
      },
      insured: {
        age: spouseAge
      },
      duration: {
        years: 5
      },
      periodLabel: 'DEATH_INSURANCE.SAFETY_NETWORK_PERIOD'
    };
  }
  return deathInsurances;
}

export function generateEducationInsurances(
  config: Config,
  userPersonalData?: PersonalData,
  userFinancialData?: FinancialData
): LifePlanInsurances {
  const userInvestorProfile =
    userFinancialData?.investorProfile ||
    config.sourceConfigurations?.investorProfileOptions.find(i => i.isDefault)?.identifier ||
    '';
  const educationInsurances: LifePlanInsurances = {};
  if (userPersonalData?.children) {
    let userAge = 0;
    if (userPersonalData?.birthDate) {
      const age = getCurrentAge(userPersonalData.birthDate);
      if (age) {
        userAge = age;
      }
    }
    userPersonalData.children.forEach((child, index) => {
      const userAgeWhenChildEducates = userAge + (config.ageMilestones.university - (child.age || 0));
      const userMilestoneWhenChildEducates = {
        userAge: userAgeWhenChildEducates,
        year: new Date().getFullYear() + userAgeWhenChildEducates - userAge
      };
      educationInsurances[child.id] = {
        id: child.id,
        name: child.name || 'EDUCATION_INSURANCE.UNKNOWN_CHILD',
        order: index,
        type: 'point-in-time',
        insurance: 'EDUCATION',
        policyHolder: {
          age: userAge,
          gender: userPersonalData?.gender || '',
          investorProfile: userInvestorProfile,
          netMonthlyIncome: userFinancialData?.netMonthlyIncome
        },
        insured: {
          age: child.age || 0,
          university: child.university,
          universityCost: child.universityCost
        },
        milestone: userMilestoneWhenChildEducates
      };
    });
  }
  return educationInsurances;
}

export function generateSavingsInsurances(
  config: Config,
  userPersonalData?: PersonalData,
  userFinancialData?: FinancialData
): LifePlanInsurances {
  let userAge = 0;
  if (userPersonalData?.birthDate) {
    const age = getCurrentAge(userPersonalData.birthDate);
    if (age) {
      userAge = age;
    }
  }
  const userInvestorProfile =
    userFinancialData?.investorProfile ||
    config.sourceConfigurations?.investorProfileOptions.find(i => i.isDefault)?.identifier ||
    '';
  const zeroSavings = {
    currency: config.defaultCurrency,
    amount: 0
  };
  const totalSavings =
    userFinancialData?.savingsAndInvestments
      ?.map(s => s.amount)
      .map(a => convert(a, config.defaultCurrency, config.currencyExchange))
      .reduce((prev, current) => {
        return {
          currency: config.defaultCurrency,
          amount: prev.amount + current.amount
        };
      }, zeroSavings) || zeroSavings;
  const savingInsurances: LifePlanInsurances = {};
  savingInsurances[ConcreteInsuranceIds.SAVING_GENERAL] = {
    id: ConcreteInsuranceIds.SAVING_GENERAL,
    name: 'SAVINGS_INSURANCE.SAFETY_NETWORK_NAME',
    order: 0,
    type: 'time-period',
    policyHolder: {
      age: userAge,
      gender: userPersonalData?.gender || '',
      investorProfile: userInvestorProfile,
      netMonthlyIncome: userFinancialData?.netMonthlyIncome
    },
    insured: {
      age: userAge
    },
    duration: {
      months: 6
    },
    periodLabel: 'SAVINGS_INSURANCE.SAFETY_NETWORK_PERIOD',
    insurance: 'SAVING'
  };
  savingInsurances[ConcreteInsuranceIds.SAVING_INVESTMENT] = {
    id: ConcreteInsuranceIds.SAVING_INVESTMENT,
    name: 'SAVINGS_INSURANCE.INVESTMENTS_NAME',
    order: (userFinancialData?.futurePlans?.length || 0) + 1,
    type: 'investment',
    policyHolder: {
      age: userAge,
      gender: userPersonalData?.gender || '',
      investorProfile: userInvestorProfile,
      netMonthlyIncome: userFinancialData?.netMonthlyIncome
    },
    insured: {
      age: userAge
    },
    totalSavings,
    insurance: 'SAVING'
  };
  if (userFinancialData?.futurePlans) {
    userFinancialData.futurePlans.forEach((futurePlan, index) => {
      const userAgeWhenPlanShouldComplete =
        userAge + parseISO(futurePlan.goalDate).getFullYear() - new Date().getFullYear();
      const userMilestoneWhenPlanShouldComplete = {
        userAge: userAgeWhenPlanShouldComplete,
        year: new Date().getFullYear() + userAgeWhenPlanShouldComplete - userAge
      };
      savingInsurances[futurePlan.id] = {
        id: futurePlan.id,
        name: config.futurePlans.find(fpType => fpType.value === futurePlan.type)?.key || '',
        order: index + 1,
        type: 'saving',
        insurance: 'SAVING',
        policyHolder: {
          age: userAge,
          gender: userPersonalData?.gender || '',
          investorProfile: userInvestorProfile,
          netMonthlyIncome: userFinancialData?.netMonthlyIncome
        },
        insured: {
          age: userAge
        },
        milestone: userMilestoneWhenPlanShouldComplete,
        totalAmount: futurePlan.goalAmount,
        savings: futurePlan.savingAmount,
        assuredAmount: futurePlan.assuredAmount,
        date: futurePlan.goalDate
      };
    });
  }
  return savingInsurances;
}

export function generateRetirementInsurances(
  config: Config,
  userPersonalData?: PersonalData,
  spousePersonalData?: PersonalData,
  userFinancialData?: FinancialData,
  spouseFinancialData?: FinancialData
): LifePlanInsurances {
  let userAge = 0;
  if (userPersonalData?.birthDate) {
    const age = getCurrentAge(userPersonalData.birthDate);
    if (age) {
      userAge = age;
    }
  }
  const userInvestorProfile =
    userFinancialData?.investorProfile ||
    config.sourceConfigurations?.investorProfileOptions.find(i => i.isDefault)?.identifier ||
    '';
  const retirementInsurances: LifePlanInsurances = {};
  retirementInsurances[ConcreteInsuranceIds.RETIREMENT_USER] = {
    id: ConcreteInsuranceIds.RETIREMENT_USER,
    name: userPersonalData?.name || 'RETIREMENT_INSURANCE.UNKNOWN_USER_NAME',
    surnames: userPersonalData?.surnames || '',
    order: 0,
    type: 'point-in-time',
    insurance: 'RETIREMENT',
    policyHolder: {
      age: userAge,
      gender: userPersonalData?.gender || '',
      investorProfile: userInvestorProfile,
      netMonthlyIncome: userFinancialData?.netMonthlyIncome
    },
    insured: {
      age: userAge
    },
    milestone: {
      userAge: config.ageMilestones.retirement,
      year: new Date().getFullYear() + config.ageMilestones.retirement - userAge
    }
  };

  if (spousePersonalData) {
    let spouseAge = 0;
    if (spousePersonalData?.birthDate) {
      const age = getCurrentAge(spousePersonalData.birthDate);
      if (age) {
        spouseAge = age;
      }
    }
    const userAgeWhenSpouseRetires = config.ageMilestones.retirement - (spouseAge - userAge);
    retirementInsurances[ConcreteInsuranceIds.RETIREMENT_SPOUSE] = {
      id: ConcreteInsuranceIds.RETIREMENT_SPOUSE,
      name: spousePersonalData.name || 'RETIREMENT_INSURANCE.UNKNOWN_SPOUSE_NAME',
      order: 1,
      type: 'point-in-time',
      insurance: 'RETIREMENT',
      policyHolder: {
        age: spouseAge,
        gender: spousePersonalData?.gender || '',
        // Investment products need the policy holder investment profile and spouse does not have investment profile
        investorProfile: userInvestorProfile,
        netMonthlyIncome: spouseFinancialData?.netMonthlyIncome
      },
      insured: {
        age: spouseAge
      },
      milestone: {
        userAge: userAgeWhenSpouseRetires,
        year: new Date().getFullYear() + userAgeWhenSpouseRetires - userAge
      }
    };
  }
  return retirementInsurances;
}

export function mixLifePlanInsurances(newTargets: LifePlanInsurances, oldTargets?: LifePlanInsurances) {
  const newTargetsCopy: LifePlanInsurances = JSON.parse(JSON.stringify(newTargets));
  if (oldTargets) {
    Object.entries(newTargetsCopy).forEach(([id, insuranceTarget]) => {
      const oldTarget = oldTargets[id];
      if (oldTarget?.currentProduct) {
        // We can only set the current product if the old one matches the temporalities from the new insurance target
        const temporalities = getProductAvailableTemporalities(
          oldTarget.currentProduct,
          oldTarget.currentProduct.selectedCurrency,
          insuranceTarget.insured.age
        );
        const selectedTemporalityFound = temporalities.find(
          t => t.id === oldTarget.currentProduct?.selectedTemporality
        );
        if (selectedTemporalityFound) {
          insuranceTarget.currentProduct = oldTarget.currentProduct;
        } else {
          insuranceTarget.currentProduct = undefined;
        }
      }
    });
  }
  return newTargetsCopy;
}

export function getCartTargets(lifePlan: LifePlan): LifePlanInsurances {
  const insuranceTargets: LifePlanInsurances = {};
  if (lifePlan.insurances) {
    Object.entries(lifePlan.insurances).forEach(([id, insuranceTarget]) => {
      return (insuranceTargets[id] = insuranceTarget);
    });
  }
  return insuranceTargets;
}

export function getTargetsOnCart(lifePlan: LifePlan): LifePlanInsurances {
  const insuranceTargets: LifePlanInsurances = {};
  if (lifePlan.insurances) {
    Object.entries(lifePlan.insurances).forEach(([id, insuranceTarget]) => {
      if (insuranceTarget.currentProduct?.cartStatus.added) {
        return (insuranceTargets[id] = insuranceTarget);
      }
      return;
    });
  }
  return insuranceTargets;
}

export function getInsuranceTargets(lifePlan: LifePlan, insurance: Insurance): LifePlanInsurances {
  const insuranceTargets: LifePlanInsurances = {};
  if (lifePlan.insurances) {
    Object.entries(lifePlan.insurances)
      .filter(([id, insuranceTarget]) => insuranceTarget.insurance === insurance && insuranceTarget.id !== 'saving-investment')
      .forEach(([id, insuranceTarget]) => (insuranceTargets[id] = insuranceTarget));
  }
  return insuranceTargets;
}

export function getInsuranceTarget(lifePlan: LifePlan, insuranceTargetId: string) {
  if (lifePlan.insurances) {
    return lifePlan.insurances[insuranceTargetId];
  }
  return undefined;
}

export function insuranceParamToInsurance(insuranceParam: string): Insurance | undefined {
  switch (insuranceParam) {
    case 'death':
      return 'DEATH';
    case 'education':
      return 'EDUCATION';
    case 'savings':
      return 'SAVING';
    case 'retirement':
      return 'RETIREMENT';
  }
  return undefined;
}

export function insuranceToInsuranceParam(insurance: Insurance): string {
  switch (insurance) {
    case 'DEATH':
      return 'death';
    case 'EDUCATION':
      return 'education';
    case 'SAVING':
      return 'savings';
    case 'RETIREMENT':
      return 'retirement';
    case 'DECUMULATION':
      return 'decumulation';
  }
}
